import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { formatMoney, formatPct } from "@/lib/format";
import DecisionBadge from "@/components/DecisionBadge";
import StatusToggle from "@/components/StatusToggle";
import InlineRank from "@/components/InlineRank";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import usePullToRefresh from "@/lib/usePullToRefresh";

export default function DayView() {
  const { date } = useParams();
  const [data, setData] = useState(null);
  const [realOnly, setRealOnly] = useState(false);
  const [switching, setSwitching] = useState(false);

  const load = () => api.get(`/portfolio/day/${date}`).then((r) => setData(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [date]);
  const ptr = usePullToRefresh(load);

  const toggleStrategy = async () => {
    if (!data || switching) return;
    const next = data.strategy === "filet" ? "sniper" : "filet";
    setSwitching(true);
    try {
      await api.patch(`/day-state/${date}`, { strategy: next });
      await load();
      toast.success(`Stratégie du ${date} changée en ${next === "filet" ? "Filet" : "Sniper"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible de changer la stratégie.");
    } finally {
      setSwitching(false);
    }
  };
  if (!data) return <div className="text-muted-1">Chargement…</div>;

  const filteredStocks = realOnly
    ? data.stocks.filter((s) => (s.status || "test").toLowerCase() === "real")
    : data.stocks;

  return (
    <div className="space-y-5 sm:space-y-6 relative">
      {/* iter 28.7 — pull-to-refresh indicator */}
      <div className="fixed top-0 left-1/2 -translate-x-1/2 z-30 pointer-events-none" style={ptr.indicatorStyle} data-testid="ptr-indicator">
        <div className="mt-2 px-3 py-1.5 rounded-full bg-card-2 border border-line-strong text-[10px] uppercase tracking-widest text-muted-1 flex items-center gap-1.5">
          <RefreshCw size={11} className={ptr.refreshing ? "animate-spin" : ""} style={{ transform: `rotate(${ptr.progress * 360}deg)` }} />
          {ptr.refreshing ? "Rafraîchissement…" : ptr.progress >= 1 ? "Relâche pour rafraîchir" : "Tire pour rafraîchir"}
        </div>
      </div>
      <Link to="/history" className="text-xs text-muted-1 hover:text-app transition-colors flex items-center gap-1.5">
        <ArrowLeft size={14} /> retour à l'historique
      </Link>
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] sm:text-xs uppercase tracking-widest text-muted-2 mb-1">Journée</div>
          <h1 className="font-display text-2xl sm:text-3xl font-semibold font-mono-num">{data.date}</h1>
          <div className="mt-1.5 flex items-center gap-2">
            <span className="text-[9px] uppercase tracking-widest text-muted-2">Stratégie</span>
            <span
              className={`px-2 py-0.5 rounded-full text-[11px] font-display border ${
                data.strategy === "filet"
                  ? "text-invest border-invest/50 bg-invest/5"
                  : "text-app border-line-strong bg-card-1"
              }`}
              data-testid="dayview-strategy-badge"
            >
              {data.strategy === "filet" ? "Filet" : "Sniper"}
            </span>
            <button
              type="button"
              onClick={toggleStrategy}
              disabled={switching}
              className="ml-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest border border-line text-muted-1 hover:text-app hover:border-line-strong transition-colors disabled:opacity-40"
              title={`Basculer vers ${data.strategy === "filet" ? "Sniper" : "Filet"} (recalcul immédiat)`}
              data-testid="dayview-strategy-toggle"
            >
              <RefreshCw size={10} className={switching ? "animate-spin" : ""} />
              → {data.strategy === "filet" ? "Sniper" : "Filet"}
            </button>
          </div>
        </div>
        <label className="flex items-center gap-2 text-[10px] sm:text-xs text-muted-1 cursor-pointer select-none" data-testid="dayview-real-only-label">
          <Switch checked={realOnly} onCheckedChange={setRealOnly} className="scale-75 origin-left" data-testid="dayview-real-only-switch" />
          <span className={realOnly ? "text-invest" : ""}>Réel uniquement</span>
        </label>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-2.5 sm:gap-4">
        <div className="bg-card-1 border border-line rounded-xl p-3 sm:p-6">
          <div className="text-[9px] sm:text-[10px] uppercase tracking-widest text-muted-2 mb-2 sm:mb-3">Allocation totale</div>
          <div className="font-mono-num text-lg sm:text-2xl">{formatMoney(data.pnl.total_allocation)}</div>
        </div>
        <div className="bg-card-1 border border-line rounded-xl p-3 sm:p-6">
          <div className="text-[9px] sm:text-[10px] uppercase tracking-widest text-muted-2 mb-2 sm:mb-3">P&L (frais inclus)</div>
          <div className={`font-mono-num text-lg sm:text-2xl ${data.pnl.pnl >= 0 ? "text-invest" : "text-ignore"}`}>
            {formatMoney(data.pnl.pnl)}
          </div>
        </div>
        <div className="bg-card-1 border border-line rounded-xl p-3 sm:p-6 col-span-2 md:col-span-1">
          <div className="text-[9px] sm:text-[10px] uppercase tracking-widest text-muted-2 mb-2 sm:mb-3">Frais estimés</div>
          <div className="font-mono-num text-lg sm:text-2xl text-muted-1">{data.settings.fee_percent} %</div>
        </div>
      </div>

      <div className="bg-card-1 border border-line rounded-xl overflow-hidden">
        <div className="grid grid-cols-[32px_1fr_56px_68px_1fr] sm:grid-cols-[44px_1fr_72px_90px_90px_90px] gap-x-3 sm:gap-4 px-3 sm:px-6 py-2 sm:py-3 border-b border-line text-[9px] sm:text-[10px] uppercase tracking-widest text-muted-2">
          <div>#</div><div>Symb.</div>
          <div className="text-right">Score</div>
          <div className="text-right">Alloc</div>
          <div className="text-right"><span className="sm:hidden">Gain / P&L</span><span className="hidden sm:inline">Gain</span></div>
          <div className="hidden sm:block text-right">P&L</div>
        </div>
        {filteredStocks.map((s) => {
          const p = data.pnl.detail.find((d) => d.stock_id === s.id);
          const gainPct = p?.gain_pct;
          const pnl = p?.pnl;
          return (
            <Link
              key={s.id}
              to={`/stock/${s.id}`}
              className="grid grid-cols-[32px_1fr_56px_68px_1fr] sm:grid-cols-[44px_1fr_72px_90px_90px_90px] gap-x-3 sm:gap-4 px-3 sm:px-6 py-2.5 sm:py-4 border-b border-line last:border-b-0 hover:bg-card-2 transition-colors items-center"
              data-testid={`day-row-${s.symbol}`}
            >
              <div className="flex items-center"><InlineRank stock={s} onChanged={load} /></div>
              <div className="min-w-0">
                <div className="font-display font-semibold text-sm sm:text-base truncate">{s.symbol}</div>
                <div className="text-[10px] sm:text-xs text-muted-2 truncate hidden sm:block">{s.name || " "}</div>
                <div className="mt-1"><StatusToggle stock={s} /></div>
              </div>
              <div className="text-right">
                <div className="font-mono-num text-sm sm:text-base leading-tight">{s.score}</div>
                <div className="mt-0.5 sm:mt-1"><DecisionBadge score={s.score} size="sm" /></div>
              </div>
              <div className="text-right font-mono-num text-xs sm:text-base">{formatMoney(s.effective_allocation)}</div>
              {/* Mobile: Gain% + P&L stacked. Desktop: two columns. */}
              <div className={`text-right font-mono-num text-sm sm:text-base leading-tight ${gainPct == null ? "text-muted-2" : gainPct >= 0 ? "text-invest" : "text-ignore"}`}>
                <div className="font-medium">{gainPct == null ? "—" : formatPct(gainPct)}</div>
                <div className={`sm:hidden text-xs mt-0.5 ${pnl == null ? "text-muted-2" : pnl >= 0 ? "text-invest" : "text-ignore"}`}>
                  {pnl == null ? "—" : formatMoney(pnl)}
                </div>
              </div>
              <div className={`hidden sm:block text-right font-mono-num text-xs sm:text-base ${pnl == null ? "text-muted-2" : pnl >= 0 ? "text-invest" : "text-ignore"}`}>
                {pnl == null ? "—" : formatMoney(pnl)}
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
